import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy } from '@angular/core';

import { CourtDurationOption } from '../court-grid-view/court-grid.model';

/**
 * One row of the per-court duration picker (Einfache Ansicht, "Platz buchen"
 * card). Built by the booking container from `selectedSlot.courts.breakdown`
 * (backend order) + the grid pivot's per-court duration feasibility.
 */
export interface CourtRowModel {
  courtId: string;
  courtName: string;
  /** One entry per filtered duration — availability flag + price + endTimeUtc. */
  options: CourtDurationOption[];
  /** True when no option is available — renders dashed/dimmed with "belegt". */
  fullyBooked: boolean;
  /**
   * True when the court is CLOSED at this slot (Platzsperre / suppressed
   * occupancy, plan §6.3.4) rather than merely booked out. Only changes the
   * word shown next to the court name — "gesperrt" instead of "belegt";
   * `fullyBooked` still governs whether the row renders its duration buttons.
   */
  blocked?: boolean;
}

/** Emitted when the user picks an available duration on a specific court. */
export interface CourtDurationSelectionEvent {
  courtId: string;
  courtName: string;
  /** The picked duration option (always `available === true`). */
  option: CourtDurationOption;
}

@Component({
  selector: 'app-duration-picker',
  standalone: true,
  imports: [],
  templateUrl: './duration-picker.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./duration-picker.component.css']
})
export class DurationPickerComponent {
  @Input() courtRows: CourtRowModel[] = [];
  @Input() selectedCourtId: string | null = null;
  @Input() selectedDurationMinutes: number | null = null;
  @Input() loading = false;

  @Output() selectionChange = new EventEmitter<CourtDurationSelectionEvent>();

  private readonly priceFormatter = new Intl.NumberFormat('de-DE', {
    style: 'currency',
    currency: 'EUR',
  });

  isRowSelected(row: CourtRowModel): boolean {
    return !row.fullyBooked && row.courtId === this.selectedCourtId;
  }

  isOptionSelected(row: CourtRowModel, option: CourtDurationOption): boolean {
    return (
      this.isRowSelected(row) &&
      option.available &&
      option.duration === this.selectedDurationMinutes
    );
  }

  selectOption(row: CourtRowModel, option: CourtDurationOption): void {
    if (!option.available) {
      return;
    }
    this.selectionChange.emit({
      courtId: row.courtId,
      courtName: row.courtName,
      option,
    });
  }

  formatPrice(price: number): string {
    return this.priceFormatter.format(price);
  }
}
